import { Component, OnInit, OnChanges, SimpleChanges, Input, Output, EventEmitter, TemplateRef, HostListener } from '@angular/core';

export interface AddButton {
  text: string;
  callback: () => void;
}

export interface TableColumn {
  field: string;
  header: string;
  sortable?: boolean;
  template?: TemplateRef<any>;
  filterable?: boolean;
  filterOptions?: Array<string | TableFilterOption>;
  filterPlaceholder?: string;
}

export interface TableFilterOption {
  label: string;
  value: any;
}

export interface TableFilterChange {
  field: string;
  value: any;
}

export interface TableAction {
  icon?: string;
  text?: string | ((row: any) => string);
  tooltip: string;
  callback: (row: any) => void;
  color?: string;
  condition?: (row: any) => boolean;
  bordered?: boolean;
  borderColor?: string;
}

@Component({
  selector: 'app-table',
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.sass']
})
export class TableComponent implements OnInit, OnChanges {

  @Input() columns: TableColumn[] = [];
  @Input() data: any[] = [];
  @Input() selectable: boolean = true;
  @Input() searchable: boolean = true;
  @Input() searchPlaceholder: string = 'Search';
  @Input() actions: TableAction[] = [];
  @Input() showDownload: boolean = true;
  @Input() showExcelDownload: boolean = true;
  @Input() showPdfDownload: boolean = true;
  @Input() pagination: boolean = true;
  @Input() pageSize: number = 5;
  @Input() serverSide: boolean = false;
  @Input() pageIndex: number = 0;
  @Input() totalItems: number = 0;
  @Input() showAddButton: boolean = false;
  @Input() addButtonText: string = 'Add';
  @Input() addButtons: AddButton[] = [];
  @Input() showFilters: boolean = false;
  @Input() showNavButton: boolean = false;
  @Input() navButtonText: string = 'Navigate';

  @Output() rowSelect = new EventEmitter<any[]>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() refresh = new EventEmitter<void>();
  @Output() downloadExcel = new EventEmitter<void>();
  @Output() downloadPdf = new EventEmitter<void>();
  @Output() add = new EventEmitter<void>();
  @Output() nav = new EventEmitter<void>();
  @Output() filterChange = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<number>();

  selectedRows: Set<any> = new Set();
  searchText: string = '';
  currentPage: number = 1;
  showDownloadMenu: boolean = false;
  openDropdown: string | null = null;
  filters: { [key: string]: any } = {};

  get allSelected(): boolean {
    return this.filteredData.length > 0 && this.selectedRows.size === this.filteredData.length;
  }

  get filteredData(): any[] {
    if (this.serverSide) return this.data;
    let result = this.data;
    
    // Apply search filter
    if (this.searchText) {
      const search = this.searchText.toLowerCase();
      result = result.filter(row => 
        this.columns.some(col => 
          String(row[col.field] || '').toLowerCase().includes(search)
        )
      );
    }
    
    // Apply column filters
    Object.keys(this.filters).forEach(field => {
      const filterValue = this.filters[field];
      if (filterValue && filterValue !== 'All') {
        result = result.filter(row => row[field] === filterValue);
      }
    });
    
    return result;
  }

  getFilterDisplayText(column: TableColumn): string {
    const currentValue = this.filters[column.field];

    if (!currentValue || currentValue === 'All') {
      //show placeholder
      return column.filterPlaceholder || `Filter by ${column.header}`;
    }

    //shoe selected value
    const selected = column.filterOptions?.find(
      option => this.getFilterOptionValue(option) === currentValue
    );
    return selected ? this.getFilterOptionLabel(selected) : String(currentValue);
  }

  getFilterOptionLabel(option: string | TableFilterOption): string {
    return typeof option === 'string' ? option : option.label;
  }

  getFilterOptionValue(option: string | TableFilterOption): any {
    return typeof option === 'string' ? option : option.value;
  }

  get paginatedData(): any[] {
    if (!this.pagination || this.serverSide) return this.filteredData;
    const start = (this.currentPage - 1) * this.pageSize;
    return this.filteredData.slice(start, start + this.pageSize);
  }

  get totalPages(): number {
    const total = this.serverSide ? this.totalItems : this.filteredData.length;
    return Math.ceil(total / this.pageSize);
  }

  toggleDropdown(field: string): void {
    this.openDropdown = this.openDropdown === field ? null : field;
  }

  selectOption(field: string, value: any): void {
    this.filters[field] = value;
    this.onFilterChange(field, value);
    this.openDropdown = null;
  }

  onFilterChange(field: string, value: any): void {
    this.filters[field] = value;
    this.currentPage = 1;
    this.filterChange.emit(
      this.serverSide ? ({ field, value } as TableFilterChange) : this.filteredData
    );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-select')) {
      this.openDropdown = null;
    }
  }



  toggleSelectAll(): void {
    if (this.allSelected) {
      this.selectedRows.clear();
    } else {
      this.data.forEach(row => this.selectedRows.add(row));
    }
    this.emitSelection();
  }

  toggleRow(row: any): void {
    if (this.selectedRows.has(row)) {
      this.selectedRows.delete(row);
    } else {
      this.selectedRows.add(row);
    }
    this.emitSelection();
  }

  isSelected(row: any): boolean {
    return this.selectedRows.has(row);
  }

  onSearch(value: string): void {
    this.searchText = value;
    this.currentPage = 1;
    this.searchChange.emit(value);
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      if (this.serverSide) this.pageChange.emit(this.currentPage - 1);
    }
  }

  previousPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      if (this.serverSide) this.pageChange.emit(this.currentPage - 1);
    }
  }

  private emitSelection(): void {
    this.rowSelect.emit(Array.from(this.selectedRows));
  }

  executeAction(action: TableAction, row: any): void {
    action.callback(row);
  }

  getActionText(action: TableAction, row: any): string {
    if (typeof action.text === 'function') {
      return action.text(row);
    }
    return action.text || '';
  }

  toggleDownloadMenu(): void {
    this.showDownloadMenu = !this.showDownloadMenu;
  }

  onDownloadExcel(): void {
    this.downloadExcel.emit();
    this.showDownloadMenu = false;
  }

  onDownloadPdf(): void {
    this.downloadPdf.emit();
    this.showDownloadMenu = false;
  }

  refreshData(): void {
    this.refresh.emit();
  }

  onAdd(): void {
    this.add.emit();
  }

  onAddButton(button: AddButton): void {
    button.callback();
  }

  onNav(): void {
    this.nav.emit();
  }

  constructor() { }

  ngOnInit(): void {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.pageIndex && this.serverSide) {
      this.currentPage = this.pageIndex + 1;
    }
  }

}
